import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useSocketStore } from '../stores/socketStore';
import { usePremium } from '../hooks/usePremium';
import { Music, User, Trophy, LogOut, Wifi, WifiOff, AlertTriangle, Crown, Gamepad2, Users } from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { player, isAuthenticated, logout } = useAuthStore();
  const { isConnected, currentRoom, leaveRoom, publicRooms, fetchPublicRooms } = useSocketStore();
  const { isPremium } = usePremium();
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [onlineCount, setOnlineCount] = useState(0);

  // Charger les rooms publiques et le nombre de joueurs en ligne
  useEffect(() => {
    if (isConnected) {
      fetchPublicRooms();
      // Fetch online player count
      const fetchOnline = () => {
        fetch('/api/stats').then(r => r.json()).then(d => {
          setOnlineCount(d.playersOnline || 0);
        }).catch(() => {});
      };
      fetchOnline();
      const interval = setInterval(() => { fetchPublicRooms(); fetchOnline(); }, 15000);
      return () => clearInterval(interval);
    }
  }, [isConnected, fetchPublicRooms]);
  
  // Vérifie si on est dans une room (page /room/* ou /game/*)
  const isInRoom = location.pathname.startsWith('/room/') || location.pathname.startsWith('/game/');
  
  // Handler pour les liens qui doivent vérifier si on est dans une room
  const handleNavClick = (e: React.MouseEvent, to: string) => {
    if (isInRoom && currentRoom) {
      e.preventDefault();
      setPendingNavigation(to);
      setShowLeaveModal(true);
    }
  };
  
  // Confirmer et quitter la room
  const handleConfirmLeave = () => {
    leaveRoom();
    setShowLeaveModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };
  
  // Annuler
  const handleCancelLeave = () => {
    setShowLeaveModal(false);
    setPendingNavigation(null);
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            onClick={(e) => handleNavClick(e, '/')}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Music className="w-6 h-6 text-white" />
            </div>
            <span className="font-display font-bold text-xl gradient-text hidden sm:block">
              BlindTest
            </span>
          </Link>
          
          {/* Navigation */}
          <nav className="flex items-center gap-2">
            {/* Bouton Parties */}
            <NavLink 
              to="/games" 
              active={location.pathname === '/games'}
              onClick={(e) => handleNavClick(e, '/games')}
              badge={publicRooms.length > 0 ? publicRooms.length : undefined}
            >
              <Gamepad2 className="w-5 h-5" />
              <span className="hidden sm:inline">Parties</span>
            </NavLink>
            
            {/* Bouton Premium */}
            <NavLink 
              to="/premium" 
              active={location.pathname === '/premium'}
              onClick={(e) => handleNavClick(e, '/premium')}
              premium={!isPremium}
            >
              <Crown className={`w-5 h-5 ${isPremium ? 'text-yellow-400' : ''}`} />
              <span className="hidden sm:inline">
                {isPremium ? 'Premium' : 'Premium'}
              </span>
            </NavLink>
            
            <NavLink 
              to="/leaderboard" 
              active={location.pathname === '/leaderboard'}
              onClick={(e) => handleNavClick(e, '/leaderboard')}
            >
              <Trophy className="w-5 h-5" />
              <span className="hidden sm:inline">Classement</span>
            </NavLink>
            
            {isAuthenticated && player ? (
              <>
                <NavLink 
                  to="/profile" 
                  active={location.pathname === '/profile'}
                  onClick={(e) => handleNavClick(e, '/profile')}
                >
                  {player.avatar ? (
                    <img src={player.avatar} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <User className="w-5 h-5" />
                  )}
                  <span className="hidden sm:inline">{player.name}</span>
                </NavLink>
                <button
                  onClick={logout}
                  className="p-2 text-dark-400 hover:text-white transition-colors"
                  title="Déconnexion"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <Link 
                to="/" 
                className="btn-primary text-sm py-2 px-4"
                onClick={(e) => handleNavClick(e, '/')}
              >
                Jouer
              </Link>
            )}
            
            {/* Online count + Status */}
            {onlineCount > 0 && (
              <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 bg-success/10 rounded-lg border border-success/20">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                <Users className="w-3.5 h-3.5 text-success" />
                <span className="text-success text-xs font-semibold">{onlineCount}</span>
              </div>
            )}
            <div className="ml-1" title={isConnected ? 'Connecté' : 'Déconnecté'}>
              {isConnected ? (
                <Wifi className="w-5 h-5 text-success" />
              ) : (
                <WifiOff className="w-5 h-5 text-error animate-pulse" />
              )}
            </div>
          </nav>
        </div>
      </header>
      
      {/* Main content */}
      <main className="flex-1 container mx-auto px-4 py-6">
        {children}
      </main>
      
      {/* Footer */}
      <footer className="glass-dark py-6 mt-auto">
        <div className="container mx-auto px-4 flex flex-col items-center gap-3">
          <a href="https://bluehoodgames.com" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-2.5 opacity-70 hover:opacity-100 transition-all">
            <img src="/bluehood-icon.png" alt="Bluehood Games" className="h-10 w-10 bluehood-glow" />
            <span className="text-dark-300 text-sm font-semibold group-hover:text-blue-400 transition-colors">Bluehood Games</span>
          </a>
          <p className="text-dark-500 text-xs">&copy; {new Date().getFullYear()} TheBlindTest — Tous droits reserves</p>
        </div>
      </footer>
      <style>{`
        @keyframes bluehood-pulse {
          0%, 100% { filter: drop-shadow(0 0 4px rgba(0, 200, 255, 0.3)); }
          25% { filter: drop-shadow(0 0 12px rgba(0, 200, 255, 0.8)) drop-shadow(0 0 20px rgba(99, 102, 241, 0.4)); }
          50% { filter: drop-shadow(0 0 6px rgba(99, 102, 241, 0.5)); }
          75% { filter: drop-shadow(0 0 14px rgba(0, 200, 255, 0.7)) drop-shadow(0 0 25px rgba(59, 130, 246, 0.3)); }
        }
        .bluehood-glow { animation: bluehood-pulse 3s ease-in-out infinite; }
      `}</style>
      
      {/* Modal de confirmation pour quitter la room */}
      {showLeaveModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
          <div className="bg-dark-800 rounded-2xl p-6 max-w-md w-full shadow-xl border border-dark-700">
            <div className="flex items-center gap-3 mb-4 text-warning">
              <AlertTriangle className="w-8 h-8" />
              <h2 className="font-display text-xl font-bold">Quitter la partie ?</h2>
            </div>
            <p className="text-dark-300 mb-6">
              Vous êtes actuellement dans une room. Si vous quittez maintenant, vous perdrez votre progression.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelLeave}
                className="flex-1 btn-secondary"
              >
                Rester
              </button>
              <button
                onClick={handleConfirmLeave}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-xl transition-all"
              >
                Quitter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Composant NavLink
function NavLink({ 
  to, 
  children, 
  active,
  onClick,
  premium,
  badge,
}: { 
  to: string; 
  children: ReactNode; 
  active: boolean;
  onClick?: (e: React.MouseEvent) => void;
  premium?: boolean;
  badge?: number;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`
        relative flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200
        ${premium 
          ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 border border-yellow-500/30 hover:border-yellow-400'
          : active 
            ? 'bg-primary-500/20 text-primary-400' 
            : 'text-dark-300 hover:text-white hover:bg-white/5'
        }
      `}
    >
      {children}
      {badge !== undefined && badge > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-success text-white text-xs font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}
